import ReactMarkdown from 'react-markdown/react-markdown.min'
import remarkGfm from 'remark-gfm'
import { ExternalLinkIcon } from '@heroicons/react/outline'
import { useProposalGovernanceQuery } from 'hooks/useProposal'
import ProposalStateBadge from '@components/ProposalStateBadge'
import { TransactionPanel } from '@components/instructions/TransactionPanel'
import DiscussionPanel from 'components/chat/DiscussionPanel'
import VotePanel from '@components/VotePanel'
import { ApprovalProgress, VetoProgress } from '@components/QuorumProgress'
import useRealm from 'hooks/useRealm'
import useProposalVotes from 'hooks/useProposalVotes'
import ProposalTimeStatus from 'components/ProposalTimeStatus'
import { useEffect, useState } from 'react'
import ProposalActionsPanel from '@components/ProposalActions'
import { getRealmExplorerHost } from 'tools/routing'
import {
  GovernanceAccountType,
  ProposalState,
  VoteType,
} from '@realms-today/spl-governance'
import VoteResultStatus from '@components/VoteResultStatus'
import VoteResults from '@components/VoteResults'
import MultiChoiceVotes from '@components/MultiChoiceVotes'
import { resolveProposalDescription } from '@utils/helpers'
import PreviousRouteBtn from '@components/PreviousRouteBtn'
import Link from 'next/link'
import useQueryContext from '@hooks/useQueryContext'
import { ChevronRightIcon } from '@heroicons/react/solid'
import ProposalExecutionCard from '@components/ProposalExecutionCard'
import ProposalVotingPower from '@components/ProposalVotingPower'
import { useMediaQuery } from 'react-responsive'
import NftProposalVoteState from 'NftVotePlugin/NftProposalVoteState'
import ProposalWarnings from './ProposalWarnings'
import useWalletOnePointOh from '@hooks/useWalletOnePointOh'
import VotingRules from '@components/VotingRules'
import { useRouteProposalQuery } from '@hooks/queries/proposal'

const Proposal = () => {
  const { realmInfo, symbol } = useRealm()
  const proposal = useRouteProposalQuery().data?.result
  const governance = useProposalGovernanceQuery().data?.result
  const descriptionLink = proposal?.account.descriptionLink
  const allowDiscussion = realmInfo?.allowDiscussion ?? true
  const isMulti =
    proposal?.account.voteType !== VoteType.SINGLE_CHOICE &&
    proposal?.account.accountType === GovernanceAccountType.ProposalV2

  const [description, setDescription] = useState('')
  const voteData = useProposalVotes(proposal?.account)
  const currentWallet = useWalletOnePointOh()
  const showResults =
    proposal &&
    proposal.account.state !== ProposalState.Cancelled &&
    proposal.account.state !== ProposalState.Draft

  const votingEnded =
    !!governance &&
    !!proposal &&
    proposal.account.getTimeToVoteEnd(governance.account) < 0

  const isTwoCol = useMediaQuery({ query: '(min-width: 768px)' })

  useEffect(() => {
    const handleResolveDescription = async () => {
      const description = await resolveProposalDescription(descriptionLink!)
      setDescription(description)
    }
    if (descriptionLink) {
      handleResolveDescription()
    } else {
      setDescription('')
    }
  }, [descriptionLink])

  const { fmtUrlWithCluster } = useQueryContext()
  const showTokenBalance = proposal
    ? proposal.account.state === ProposalState.Draft ||
      proposal.account.state === ProposalState.SigningOff ||
      (proposal.account.state === ProposalState.Voting && !votingEnded)
    : true
  const showProposalExecution =
    proposal &&
    (proposal.account.state === ProposalState.Succeeded ||
      proposal.account.state === ProposalState.Executing ||
      proposal.account.state === ProposalState.ExecutingWithErrors)

  const proposalPublicKey = proposal?.pubkey.toBase58()
  const originalCustomProposal = '9R58tgzZKfxxd5LQRFdfyJpYmaFRSGbAJBM8DCSpHySg'
  const revisedCustomProposal = '4bv3rK5qEhoFmFRtxJp2fh5342uwh3VKUfLbqkyuzYYV'
  const showCustomProposal =
    proposalPublicKey === originalCustomProposal ||
    proposalPublicKey === revisedCustomProposal

  return (
    <div className="grid grid-cols-12 gap-4 overflow-y-auto">
      <div className="bg-bkg-2 rounded-lg p-4 md:p-6 col-span-12 md:col-span-7 lg:col-span-8 space-y-3">
        {proposal ? (
          <>
            <div className="flex flex-items justify-between">
              <PreviousRouteBtn />
              <div className="flex items-center">
                <a
                  href={`https://${getRealmExplorerHost(
                    realmInfo
                  )}/#/proposal/${proposal.pubkey.toBase58()}?programId=${proposal.owner.toBase58()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLinkIcon className="flex-shrink-0 h-4 ml-2 mt-0.5 text-primary-light w-4" />
                </a>
              </div>
            </div>

            <div className="py-4">
              <div className="flex items-center justify-between mb-1">
                <h1 className="mr-2 overflow-wrap-anywhere">
                  {proposal?.account.name}
                </h1>
                <ProposalStateBadge proposal={proposal.account} />
              </div>
            </div>

            {description && (
              <div className="pb-2">
                {!showCustomProposal && (
                  <ReactMarkdown
                    className="markdown"
                    linkTarget="_blank"
                    remarkPlugins={[remarkGfm]}
                  >
                    {description}
                  </ReactMarkdown>
                )}

                {showCustomProposal && (
                  <>
                    {proposalPublicKey === originalCustomProposal && (
                      <>
                        $MPLX serves as the Metaplex Foundation’s multi-faceted,
                        native utility token for an open metaverse. While $MPLX
                        will play multiple roles throughout the ecosystem, one
                        primary function is coordinating governance and upgrades
                        to the Metaplex Protocol. Holders of $MPLX will have the
                        opportunity to vote in the Metaplex DAO, an on-chain
                        program built on the governance framework provided by
                        the Solana Program Library (SPL).
                        <br /> <br />
                        On September 20, 2022, the Metaplex Token ($MPLX) was
                        airdropped to eligible developers, creators and artists
                        through our{' '}
                        <a
                          href="https://claim.metaplex.com/"
                          className="underline text-green"
                        >
                          claim site
                        </a>
                        . The Metaplex DAO aims to build the most inclusive DAO
                        in Web3, and has proposed to extend the airdrop with 40
                        million tokens to the NFT collectors who have most
                        actively supported Metaplex NFT creators and
                        communities.
                        <br />
                        <br />
                        The criteria are:
                        <ul className="list-disc list-inside">
                          <li>User of 4+ Metaplex programs</li>
                          <li>Candy Machine v1 Minters (30-50 mints)</li>
                          <li>Candy Machine v1 Minters (51+ mints)</li>
                          <li>Candy Machine v2 Minters (15-30 mints)</li>
                          <li>Candy Machine v2 Minters (31+ mints)</li>
                        </ul>
                        <br />
                        It’s worth noting that many early creators and
                        developers will likely qualify again for the collector
                        airdrop, too.
                        <br />
                        <br />
                        This proposal will broaden token ownership to align
                        early adopters and power users of Metaplex programs to
                        ensure their voices are heard as the protocol evolves.
                      </>
                    )}

                    {proposalPublicKey === revisedCustomProposal && (
                      <div className="markdown">
                        <p>
                          Collectors are the lifeblood of the Metaplex
                          ecosystem. The Metaplex Foundation is upgrading its
                          collector airdrop proposal for a new vote in the
                          Metaplex DAO.
                        </p>
                        <br />
                        <h2>What’s new:</h2>
                        <ul>
                          <li>
                            If the majority of voting $MPLX token holders
                            approve this proposal, it will pass, regardless of
                            quorum.
                          </li>
                          <li>
                            The Foundation is incorporating more collectors by
                            lowering the mint count thresholds and adding those
                            who minted on additional primary sales contracts
                            such as the original Auction program and Fixed price
                            sale contracts.
                          </li>
                        </ul>
                        <br />
                        <h2>Full proposal below:</h2>
                        <p>
                          $MPLX serves as the Metaplex Foundation’s
                          multi-faceted, native utility token for an open
                          metaverse. While $MPLX will play multiple roles
                          throughout the ecosystem, one primary function is
                          coordinating governance and upgrades to the Metaplex
                          Protocol. Holders of $MPLX will have the opportunity
                          to vote in the Metaplex DAO, an on-chain program built
                          on the governance framework provided by the Solana
                          Program Library (SPL).
                        </p>
                        <br />
                        <p>
                          On September 20, 2022, the Metaplex Foundation
                          allocated 14 million $MPLX to airdrop to eligible
                          developers, creators and artists through our claim
                          site. The Metaplex DAO aims to build the most
                          inclusive DAO in Web3, and has proposed to extend the
                          airdrop with another 40 million tokens to the NFT
                          collectors who have most actively supported Metaplex
                          NFT creators and communities.
                        </p>
                        <br />
                        <p>The criteria are:</p>
                        <ul>
                          <li>Fixed Price Sale Minters</li>
                          <li>Auction Minters (5-14 mints)</li>
                          <li>Auction Minters (15+ mints)</li>
                          <li>Candy Machine v1 Minters (5-14 mints)</li>
                          <li>Candy Machine v1 Minters (15-29 mints)</li>
                          <li>Candy Machine v1 Minters (30-49 mints)</li>
                          <li>Candy Machine v1 Minters (50+ mints)</li>
                          <li>Candy Machine v2 Minters (5-14 mints)</li>
                          <li>Candy Machine v2 Minters (15-29 mints)</li>
                          <li>Candy Machine v2 Minters (30+ mints)</li>
                          <li>User of 4+ Metaplex programs</li>
                        </ul>
                        <br />
                        <p>
                          It’s worth noting that many early creators and
                          developers will likely qualify again for the collector
                          airdrop, too.
                        </p>
                        <br />
                        <p>
                          This proposal will broaden token ownership to align
                          early adopters and power users of Metaplex programs to
                          ensure their voices are heard as the protocol evolves.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {proposal.account && (
              <ProposalWarnings proposal={proposal.account} />
            )}
            <TransactionPanel />
            {isTwoCol && allowDiscussion && <DiscussionPanel />}
          </>
        ) : (
          <>
            <div className="animate-pulse bg-bkg-3 h-12 rounded-lg" />
            <div className="animate-pulse bg-bkg-3 h-64 rounded-lg" />
            <div className="animate-pulse bg-bkg-3 h-64 rounded-lg" />
          </>
        )}
      </div>

      <div className="col-span-12 md:col-span-5 lg:col-span-4 space-y-4">
        <VotePanel />
        {showTokenBalance && <ProposalVotingPower />}
        {showResults ? (
          <div className="bg-bkg-2 rounded-lg">
            <div className="p-4 md:p-6">
              {proposal?.account.state === ProposalState.Voting ? (
                <div className="flex items-end justify-between mb-4">
                  <h3 className="mb-0">Voting Now</h3>
                  <ProposalTimeStatus proposal={proposal?.account} />
                </div>
              ) : (
                <h3 className="mb-4">Results</h3>
              )}
              {proposal?.account.state === ProposalState.Voting && !isMulti ? (
                <>
                  <div className="pb-3">
                    <ApprovalProgress
                      votesRequired={voteData.yesVotesRequired}
                      progress={voteData.yesVoteProgress}
                      showBg
                    />
                  </div>
                  {voteData._programVersion !== undefined &&
                  // @asktree: here is some typescript gore because typescript doesn't know that a number being > 3 means it isn't 1 or 2
                  voteData._programVersion !== 1 &&
                  voteData._programVersion !== 2 &&
                  voteData.veto !== undefined &&
                  (voteData.veto.voteProgress ?? 0) > 0 ? (
                    <div className="pb-3">
                      <VetoProgress
                        votesRequired={voteData.veto.votesRequired}
                        progress={voteData.veto.voteProgress}
                        showBg
                      />
                    </div>
                  ) : undefined}
                </>
              ) : (
                <div className="pb-3">
                  <VoteResultStatus />
                </div>
              )}

              {isMulti ? (
                <MultiChoiceVotes
                  proposal={proposal.account}
                  limit={proposal.account.options.length}
                />
              ) : (
                <VoteResults proposal={proposal.account} />
              )}
              {proposal && (
                <div className="flex justify-end mt-4">
                  <Link
                    href={fmtUrlWithCluster(
                      `/dao/${symbol}/proposal/${proposal.pubkey}/explore`
                    )}
                    passHref
                  >
                    <a className="text-sm flex items-center default-transition text-fgd-2 transition-all hover:text-fgd-3">
                      Explore
                      <ChevronRightIcon className="flex-shrink-0 h-6 w-6" />
                    </a>
                  </Link>
                </div>
              )}
            </div>
          </div>
        ) : null}
        <VotingRules />
        <NftProposalVoteState proposal={proposal}></NftProposalVoteState>
        {proposal && currentWallet && showProposalExecution && (
          <ProposalExecutionCard />
        )}
        <ProposalActionsPanel />
        {!isTwoCol && proposal && allowDiscussion && (
          <div className="bg-bkg-2 rounded-lg p-4 md:p-6 ">
            <DiscussionPanel />
          </div>
        )}
      </div>
    </div>
  )
}

export default Proposal
